import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { signUp, registerVehicle } from '../../firebase/authService';
import { useEnhetsregister } from '../../hooks/useEnhetsregister';
import InvoiceProfileForm from '../../components/driver/InvoiceProfileForm';
import BillingProfileForm from '../../components/orderer/BillingProfileForm';
import { ReactComponent as TippiLogoLight } from '../../assets/logo-light.svg';
import { ReactComponent as TippiLogoDark } from '../../assets/logo-dark.svg';
import { COLORS, FONTS, SHADOWS } from '../../styles/tokens';

const STEPS = { ROLE: 1, INFO: 2, VEHICLE: 3, PAYOUT: 4, BILLING: 5 };

const VEHICLE_TYPES = [
  { value: 'tipper',  label: 'Tippbil' },
  { value: 'flatbed', label: 'Flatbed' },
  { value: 'crane',   label: 'Kranbil' },
  { value: 'other',   label: 'Annet' },
];
const FUEL_TYPES = [
  { value: 'diesel',   label: 'Diesel' },
  { value: 'electric', label: 'Elektrisk' },
  { value: 'hybrid',   label: 'Hybrid' },
];

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken    = searchParams.get('invite');
  const { orgData, orgError, orgLoading, validateOrgNumber } = useEnhetsregister();

  const [step, setStep]       = useState(STEPS.ROLE);
  const [role, setRole]       = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '', confirmPassword: '', orgNumber: '',
  });
  const [vehicle, setVehicle] = useState({
    type: 'tipper', capacityTons: '', fuelType: 'diesel', registrationPlate: '',
  });

  function setField(key, val) {
    setForm(f => ({ ...f, [key]: val }));
  }

  async function handleOrgBlur() {
    if (form.orgNumber.length >= 9) await validateOrgNumber(form.orgNumber);
  }

  function validateStep2() {
    if (!form.name.trim())                      return 'Navn er påkrevd';
    if (!form.email.trim())                     return 'E-post er påkrevd';
    if (form.password.length < 6)              return 'Passord må være minst 6 tegn';
    if (form.password !== form.confirmPassword) return 'Passordene stemmer ikke overens';
    if (!form.phone.trim())                     return 'Telefonnummer er påkrevd';
    if (!orgData)                               return 'Gyldig org.nr. er påkrevd';
    return null;
  }

  function handleStep2Next() {
    const err = validateStep2();
    if (err) { setError(err); return; }
    setError('');
    if (role === 'driver') setStep(STEPS.VEHICLE);
    else handleFinish();
  }

  async function handleFinish(vehicleData) {
    setLoading(true);
    setError('');
    try {
      const user = await signUp({
        email:     form.email,
        password:  form.password,
        role,
        name:      form.name,
        phone:     form.phone,
        orgNumber: form.orgNumber.replace(/\s/g, ''),
        orgName:   orgData?.navn ?? '',
      });
      if (role === 'driver' && vehicleData) {
        await registerVehicle(user.uid, {
          type:              vehicleData.type,
          capacityTons:      Number(vehicleData.capacityTons),
          fuelType:          vehicleData.fuelType,
          registrationPlate: vehicleData.registrationPlate.toUpperCase(),
        });
      }
      if (inviteToken) {
        try {
          const functions    = getFunctions(undefined, 'europe-west1');
          const acceptInvite = httpsCallable(functions, 'acceptOrgInvite');
          await acceptInvite({ token: inviteToken });
        } catch (inviteErr) {
          // Non-fatal: user registered successfully, invite may have expired
          console.warn('[Register] invite token accept failed:', inviteErr.message);
        }
      }
      // Sjåfører får et valgfritt steg for utbetaling og fakturering; kontoen er allerede opprettet.
      if (role === 'driver') {
        setStep(STEPS.PAYOUT);
        return;
      }
      // Bestillere får et valgfritt steg for fakturaopplysninger; de kreves først ved opprettelse av oppdrag.
      if (role === 'orderer') {
        setStep(STEPS.BILLING);
        return;
      }
      navigate('/');
    } catch (err) {
      const msgs = {
        'auth/email-already-in-use': 'Denne e-posten er allerede registrert.',
        'auth/invalid-email':        'Ugyldig e-postadresse.',
        'auth/weak-password':        'Passord må være minst 6 tegn.',
      };
      setError(msgs[err.code] ?? 'Registrering feilet. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  }

  function handleVehicleSubmit(e) {
    e.preventDefault();
    if (!vehicle.capacityTons || Number(vehicle.capacityTons) <= 0)
      return setError('Angi gyldig kapasitet i tonn');
    if (!vehicle.registrationPlate.trim())
      return setError('Registreringsnummer er påkrevd');
    setError('');
    handleFinish(vehicle);
  }

  const totalSteps = role === 'driver' ? 4 : 3;

  return (
    <div style={styles.page}>
      {/* Left brand panel — desktop only */}
      <div className="tippi-brand-panel">
        <TippiLogoLight style={{ width: 88, height: 'auto', marginBottom: 32 }} />
        <h1 style={styles.brandTitle}>Tippi</h1>
        <p style={styles.brandTagline}>
          Kom i gang på noen<br />minutter
        </p>
        <StepListBrand step={step} role={role} />
        <div style={styles.brandAccentBar} />
      </div>

      {/* Right form panel */}
      <div style={styles.formPanel}>
        <div style={styles.card}>
          {/* Mobile-only logo */}
          <div className="tippi-mobile-logo">
            <TippiLogoDark style={{ width: 44, height: 'auto' }} />
            <span style={styles.mobileLogoText}>Tippi</span>
          </div>

          {error && <div style={styles.error}>{error}</div>}

          {/* ── STEG 1: Velg rolle ─────────────────────────────── */}
          {step === STEPS.ROLE && (
            <>
              <h2 style={styles.heading}>Hvem er du?</h2>
              <div style={styles.roleGrid}>
                <RoleCard
                  icon="🚛"
                  title="Sjåfør"
                  desc="Selvstendig næringsdrivende med lastebil"
                  selected={role === 'driver'}
                  onClick={() => setRole('driver')}
                />
                <RoleCard
                  icon="📋"
                  title="Bestiller"
                  desc="Anleggsplass, entreprenør eller kommune"
                  selected={role === 'orderer'}
                  onClick={() => setRole('orderer')}
                />
              </div>
              <button
                className="tippi-btn"
                style={{ ...styles.btn, opacity: role ? 1 : 0.45 }}
                onClick={() => role && setStep(STEPS.INFO)}
                disabled={!role}
              >
                Fortsett
              </button>
              <p style={styles.footer}>
                Har du konto? <Link to="/login" style={styles.link}>Logg inn</Link>
              </p>
            </>
          )}

          {/* ── STEG 2: Brukerinfo ─────────────────────────────── */}
          {step === STEPS.INFO && (
            <>
              <h2 style={styles.heading}>
                {role === 'driver' ? 'Sjåfør' : 'Bestiller'} — din informasjon
              </h2>
              <StepBars current={2} total={totalSteps} />

              <div style={styles.form}>
                <Field label="Fullt navn"           value={form.name}            onChange={v => setField('name', v)} />
                <Field label="E-post"   type="email" value={form.email}           onChange={v => setField('email', v)} />
                <Field label="Telefon"  type="tel"   value={form.phone}           onChange={v => setField('phone', v)} />
                <Field label="Passord (min. 6 tegn)" type="password" value={form.password} onChange={v => setField('password', v)} />
                <Field label="Bekreft passord"       type="password" value={form.confirmPassword} onChange={v => setField('confirmPassword', v)} />

                <label style={styles.label}>Organisasjonsnummer</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="tippi-input"
                    style={styles.input}
                    value={form.orgNumber}
                    onChange={e => setField('orgNumber', e.target.value)}
                    onBlur={handleOrgBlur}
                    placeholder="123 456 789"
                    maxLength={11}
                  />
                  {orgLoading && <span style={styles.orgSpinner}>søker…</span>}
                </div>
                {orgError && <p style={styles.orgError}>{orgError}</p>}
                {orgData && (
                  <div style={styles.orgConfirm}>
                    ✓ {orgData.navn}
                    {orgData.forretningsadresse?.poststed ? ` · ${orgData.forretningsadresse.poststed}` : ''}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                  <button className="tippi-btn-secondary" style={styles.btnSecondary} onClick={() => setStep(STEPS.ROLE)}>
                    ← Tilbake
                  </button>
                  <button className="tippi-btn" style={styles.btn} onClick={handleStep2Next} disabled={loading || orgLoading}>
                    {loading ? 'Oppretter konto…'
                      : role === 'driver' ? 'Neste: kjøretøy →' : 'Opprett konto'}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ── STEG 3: Kjøretøy ──────────────────────────────── */}
          {step === STEPS.VEHICLE && (
            <>
              <h2 style={styles.heading}>Registrer kjøretøy</h2>
              <StepBars current={3} total={totalSteps} />

              <form onSubmit={handleVehicleSubmit} style={styles.form}>
                <label style={styles.label}>Kjøretøytype</label>
                <select
                  className="tippi-input"
                  style={styles.input}
                  value={vehicle.type}
                  onChange={e => setVehicle(v => ({ ...v, type: e.target.value }))}
                >
                  {VEHICLE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>

                <Field
                  label="Kapasitet (tonn)" type="number"
                  value={vehicle.capacityTons}
                  onChange={val => setVehicle(v => ({ ...v, capacityTons: val }))}
                  min={1} max={50}
                />

                <label style={styles.label}>Drivstofftype</label>
                <select
                  className="tippi-input"
                  style={styles.input}
                  value={vehicle.fuelType}
                  onChange={e => setVehicle(v => ({ ...v, fuelType: e.target.value }))}
                >
                  {FUEL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>

                <Field
                  label="Registreringsnummer"
                  value={vehicle.registrationPlate}
                  onChange={val => setVehicle(v => ({ ...v, registrationPlate: val }))}
                  placeholder="AB 12345"
                />

                <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                  <button type="button" className="tippi-btn-secondary" style={styles.btnSecondary} onClick={() => setStep(STEPS.INFO)}>
                    ← Tilbake
                  </button>
                  <button type="submit" className="tippi-btn" style={styles.btn} disabled={loading}>
                    {loading ? 'Oppretter konto…' : 'Fullfør registrering'}
                  </button>
                </div>
              </form>
            </>
          )}

          {/* ── STEG 4: Utbetaling og fakturering (sjåfør, kan hoppes over) ── */}
          {step === STEPS.PAYOUT && (
            <>
              <h2 style={styles.heading}>Utbetaling og fakturering</h2>
              <StepBars current={4} total={totalSteps} />
              <InvoiceProfileForm
                embedded
                prefill={{ legalName: orgData?.navn ?? '', orgNumber: form.orgNumber.replace(/\s/g, '') }}
                onSaved={() => navigate('/')}
                onSkip={() => navigate('/')}
              />
            </>
          )}

          {/* ── STEG 3: Fakturaopplysninger (bestiller, kan hoppes over) ── */}
          {step === STEPS.BILLING && (
            <>
              <h2 style={styles.heading}>Fakturaopplysninger</h2>
              <StepBars current={3} total={totalSteps} />
              <BillingProfileForm
                embedded
                prefill={{ name: orgData?.navn || form.name.trim(), orgNumber: form.orgNumber.replace(/\s/g, '') }}
                onSaved={() => navigate('/')}
                onSkip={() => navigate('/')}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function RoleCard({ icon, title, desc, selected, onClick }) {
  return (
    <div
      className="tippi-choice-card"
      onClick={onClick}
      style={{
        ...styles.roleCard,
        border: `2px solid ${selected ? COLORS.primary : COLORS.border}`,
        background: selected ? COLORS.blueBg : COLORS.white,
      }}
    >
      <div style={{ fontSize: 34 }}>{icon}</div>
      <div style={{ fontWeight: 700, fontSize: 16, marginTop: 10, color: COLORS.textDark }}>{title}</div>
      <div style={{ fontSize: 13, color: COLORS.textMid, marginTop: 4, lineHeight: 1.4 }}>{desc}</div>
      {selected && (
        <div style={styles.roleSelectedDot} />
      )}
    </div>
  );
}

function StepBars({ current, total }) {
  return (
    <div className="tippi-step-bars" data-testid="step-bars">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          style={{
            height: 4, flex: 1, borderRadius: 2, maxWidth: 48,
            background: i < current ? COLORS.primary : COLORS.border,
            transition: 'background 0.2s',
          }}
        />
      ))}
    </div>
  );
}

function StepListBrand({ step, role }) {
  const steps = [
    { label: 'Velg rolle' },
    { label: 'Kontoinformasjon' },
    ...(role === 'driver' ? [{ label: 'Kjøretøy' }, { label: 'Utbetaling' }] : []),
    ...(role === 'orderer' ? [{ label: 'Fakturaopplysninger' }] : []),
  ];
  // Fakturasteget (STEPS.BILLING) er tredje trinn i bestillerlisten.
  const shownStep = step === STEPS.BILLING ? 3 : step;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {steps.map((s, i) => {
        const done    = i + 1 < shownStep;
        const current = i + 1 === shownStep;
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: done ? COLORS.accent : current ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)',
              border: `2px solid ${done || current ? COLORS.accent : 'rgba(255,255,255,0.2)'}`,
              fontSize: 11, fontWeight: 700,
              color: done ? COLORS.primary : '#fff',
            }}>
              {done ? '✓' : i + 1}
            </div>
            <span style={{
              fontSize: 14, fontWeight: current ? 600 : 400,
              color: current ? '#fff' : 'rgba(255,255,255,0.55)',
            }}>
              {s.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, min, max }) {
  return (
    <>
      <label style={styles.label}>{label}</label>
      <input
        className="tippi-input"
        style={styles.input}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        min={min}
        max={max}
      />
    </>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────

const styles = {
  page:       { minHeight: '100vh', display: 'flex', background: COLORS.bg },
  brandTitle: { fontFamily: FONTS.display, fontSize: 52, fontWeight: 700, color: '#fff', margin: '0 0 14px', letterSpacing: 1 },
  brandTagline: { fontSize: 17, color: 'rgba(255,255,255,0.68)', margin: '0 0 40px', lineHeight: 1.6 },
  brandAccentBar: { marginTop: 48, width: 40, height: 4, background: COLORS.accent, borderRadius: 2 },
  formPanel:  { flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 20px' },
  card:       { background: COLORS.white, borderRadius: 16, padding: '44px 40px', width: '100%', maxWidth: 460, boxShadow: SHADOWS.md },
  mobileLogoText: { fontFamily: FONTS.display, fontSize: 28, fontWeight: 700, color: COLORS.textDark },
  heading:    { fontFamily: FONTS.display, fontSize: 28, fontWeight: 700, color: COLORS.textDark, margin: '0 0 20px', letterSpacing: 0.3 },
  form:       { display: 'flex', flexDirection: 'column', gap: 6 },
  label:      { fontSize: 13, fontWeight: 600, color: COLORS.textMid, marginTop: 8 },
  input:      { padding: '11px 14px', borderRadius: 8, border: `1.5px solid ${COLORS.border}`, fontSize: 15, width: '100%', transition: 'border-color 0.15s' },
  btn:        { flex: 1, padding: '13px', borderRadius: 8, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { flex: 1, padding: '13px', borderRadius: 8, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, fontSize: 15, cursor: 'pointer', color: COLORS.textDark },
  error:      { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '12px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, lineHeight: 1.4 },
  orgError:   { color: COLORS.dangerText, fontSize: 13, marginTop: 2 },
  orgConfirm: { color: COLORS.success, fontSize: 13, fontWeight: 600, marginTop: 2 },
  orgSpinner: { position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: COLORS.textLight },
  roleGrid:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 },
  roleCard:   { padding: '22px 14px', borderRadius: 12, cursor: 'pointer', textAlign: 'center', position: 'relative' },
  roleSelectedDot: { position: 'absolute', top: 10, right: 10, width: 8, height: 8, borderRadius: '50%', background: COLORS.primary },
  footer:     { textAlign: 'center', marginTop: 20, fontSize: 14, color: COLORS.textMid },
  link:       { color: COLORS.primary, fontWeight: 600 },
};
